<script setup>
import { computed } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import {
  FIELD_INPUT_CLASS,
  FIELD_SELECT_CLASS,
  SURVEY_OPERATIONAL_FLOWS,
} from '../constants';
import {
  isResubmitLockedFlow,
  MAX_EXPIRES_IN_DAYS,
} from '../helper/surveyFormHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Delivery rules of the survey link: how long it stays valid and whether the
 * customer may answer more than once.
 */
const props = defineProps({
  settings: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);

const { t, validationMessage } = useSurveyLabels();

const expiresInDays = computed({
  get: () => props.settings.expires_in_days ?? '',
  set: value => {
    const parsed = Number.parseInt(value, 10);
    emit('update', {
      expires_in_days: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    });
  },
});

const allowResubmit = computed({
  get: () =>
    isResubmitLockedFlow(props.settings.operational_flow)
      ? false
      : props.settings.allow_resubmit,
  set: value =>
    emit('update', {
      allow_resubmit: isResubmitLockedFlow(props.settings.operational_flow)
        ? false
        : value,
    }),
});

const resubmissionDisabled = computed(() =>
  isResubmitLockedFlow(props.settings.operational_flow)
);

const resubmissionLockedHelper = computed(() =>
  props.settings.operational_flow === 'inspection'
    ? t('SURVEYS.EDITOR.SETTINGS.INSPECTION_RESUBMIT_DISABLED')
    : t('SURVEYS.EDITOR.SETTINGS.MAINTENANCE_RESUBMIT_DISABLED')
);

const resubmissionHelper = computed(() =>
  resubmissionDisabled.value
    ? resubmissionLockedHelper.value
    : t('SURVEYS.EDITOR.SETTINGS.ALLOW_RESUBMIT_HELPER')
);

const operationalFlow = computed({
  get: () => props.settings.operational_flow || 'generic',
  set: value =>
    emit('update', {
      operational_flow: value,
      ...(isResubmitLockedFlow(value) ? { allow_resubmit: false } : {}),
    }),
});

const locale = computed({
  get: () => (props.settings.locale === 'en' ? 'en' : 'pt_BR'),
  set: value => emit('update', { locale: value === 'en' ? 'en' : 'pt_BR' }),
});

const operationalFlowLabels = computed(() => [
  t('SURVEYS.OPERATIONAL_FLOWS.GENERIC'),
  t('SURVEYS.OPERATIONAL_FLOWS.VISIT_REQUEST'),
  t('SURVEYS.OPERATIONAL_FLOWS.PROPERTY_OFFER'),
  t('SURVEYS.OPERATIONAL_FLOWS.KYC_REQUEST'),
  t('SURVEYS.OPERATIONAL_FLOWS.LISTING_INTAKE'),
  t('SURVEYS.OPERATIONAL_FLOWS.BUYER_QUALIFICATION'),
  t('SURVEYS.OPERATIONAL_FLOWS.TENANT_QUALIFICATION'),
  t('SURVEYS.OPERATIONAL_FLOWS.OWNER_ONBOARDING'),
  t('SURVEYS.OPERATIONAL_FLOWS.INSPECTION'),
  t('SURVEYS.OPERATIONAL_FLOWS.CONTRACT_SIGNATURE'),
  t('SURVEYS.OPERATIONAL_FLOWS.MAINTENANCE_REQUEST'),
  t('SURVEYS.OPERATIONAL_FLOWS.POST_SALE_FOLLOW_UP'),
]);

const operationalFlowOptions = computed(() =>
  SURVEY_OPERATIONAL_FLOWS.map((value, index) => ({
    value,
    label: operationalFlowLabels.value[index],
  }))
);

const expiryHelper = computed(() =>
  props.settings.expires_in_days
    ? t('SURVEYS.EDITOR.SETTINGS.EXPIRES_HELPER', {
        count: props.settings.expires_in_days,
      })
    : t('SURVEYS.EDITOR.SETTINGS.EXPIRES_NEVER')
);
</script>

<template>
  <section class="grid gap-4">
    <header class="min-w-0">
      <h2
        class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        {{ t('SURVEYS.EDITOR.SETTINGS.TITLE') }}
      </h2>
      <p class="mt-1 text-lais-body-sm text-lais-text-body">
        {{ t('SURVEYS.EDITOR.SETTINGS.SUBTITLE') }}
      </p>
    </header>

    <LaisField
      :label="t('SURVEYS.EDITOR.SETTINGS.EXPIRES')"
      :helper="expiryHelper"
      :error="validationMessage(errors.expires_in_days)"
    >
      <input
        v-model="expiresInDays"
        type="number"
        min="1"
        :max="MAX_EXPIRES_IN_DAYS"
        :class="FIELD_INPUT_CLASS"
        :placeholder="t('SURVEYS.EDITOR.SETTINGS.EXPIRES_PLACEHOLDER')"
      />
    </LaisField>

    <LaisField
      :label="t('SURVEYS.EDITOR.SETTINGS.OPERATIONAL_FLOW')"
      :helper="t('SURVEYS.EDITOR.SETTINGS.OPERATIONAL_FLOW_HELPER')"
      :error="validationMessage(errors.operational_flow)"
    >
      <select v-model="operationalFlow" :class="FIELD_INPUT_CLASS">
        <option
          v-for="option in operationalFlowOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </LaisField>

    <LaisField
      :label="t('SURVEYS.EDITOR.SETTINGS.LOCALE')"
      :helper="t('SURVEYS.EDITOR.SETTINGS.LOCALE_HELPER')"
      :error="validationMessage(errors.locale)"
    >
      <select v-model="locale" name="locale" :class="FIELD_SELECT_CLASS">
        <option value="pt_BR">
          {{ t('SURVEYS.EDITOR.SETTINGS.LOCALE_PT_BR') }}
        </option>
        <option value="en">
          {{ t('SURVEYS.EDITOR.SETTINGS.LOCALE_EN') }}
        </option>
      </select>
    </LaisField>

    <LaisToggle
      v-model="allowResubmit"
      :disabled="resubmissionDisabled"
      :label="t('SURVEYS.EDITOR.SETTINGS.ALLOW_RESUBMIT')"
      :helper="resubmissionHelper"
    />
  </section>
</template>
